/**
 * The root of one tutorial illustration: sets the cycle length (`--dg-cycle`
 * drives every dg-tut-* animation inside), stamps the example label, offers
 * Replay (re-keys the children), pauses when the tab is hidden, and renders
 * the static final frame instead when the viewer prefers reduced motion.
 * For Media Devoted | Created By Ciro Autiero
 */
import { CSSProperties, FC, ReactNode, useState } from 'react';
import { RotateCcw } from 'lucide-react';

import { useReducedMotion, useVisibilityPause } from './use-motion';

export const EXAMPLE_STAMP = 'Example — the Australia day-sheet';

export const Illustration: FC<{
  /** Seconds per loop — every node's delay is a fraction of it. */
  cycleSeconds: number;
  /** The animated story. */
  children: ReactNode;
  /** What the story looks like when it is over (reduced motion). Omit when
   *  the children's motion-reduce classes already render the end state. */
  finalFrame?: ReactNode;
  stamp?: string | null;
  className?: string;
}> = ({ cycleSeconds, children, finalFrame, stamp = EXAMPLE_STAMP, className }) => {
  const frame = finalFrame ?? children;
  const [run, setRun] = useState(0);
  const reduced = useReducedMotion();
  const paused = useVisibilityPause();
  return (
    <div
      className={`relative overflow-x-auto rounded-lg border border-border/60 bg-muted/20 p-3 ${className ?? ''}`}
      style={
        {
          '--dg-cycle': `${cycleSeconds}s`,
          animationPlayState: paused ? 'paused' : 'running',
        } as CSSProperties
      }
      data-paused={paused ? 'true' : undefined}
      data-illustration=""
    >
      <div className="mb-2 flex items-center gap-2">
        {stamp ? (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {stamp}
          </span>
        ) : null}
        <span className="grow" />
        {!reduced ? (
          <button
            type="button"
            onClick={() => setRun((n) => n + 1)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            aria-label="Replay the illustration"
          >
            <RotateCcw className="h-3 w-3" /> Replay
          </button>
        ) : null}
      </div>
      {reduced ? (
        <div data-testid="illustration-final-frame">{frame}</div>
      ) : (
        <div key={run} className={paused ? '[&_*]:[animation-play-state:paused]' : ''}>
          {children}
        </div>
      )}
    </div>
  );
};
